import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import Section from '@components/Section'
import Input from '@components/Input'
import Tooltip from '@components/Tooltip'
import Button from '@components/Button'
import PhoneInput, { type PhoneInputMeta } from '@components/PhoneInput'
import Spinner from '@components/Spinner'
import Modal from '@components/Modal'
import { isEmailValid } from '@/util/helpers'
import type { Contact } from '@services/contribution/contribution.d.ts'
import type { ContactData } from '@hooks/useContacts'

export interface EditContactFormProps {
  contact: Contact
  onSubmit: (contact: ContactData) => Promise<boolean>
  onDelete?: () => Promise<boolean>
  labelStyle?: string
}

export default function EditContactForm({ contact, onSubmit, onDelete, labelStyle }: EditContactFormProps) {
  const { t } = useTranslation()
  const [name, setName] = useState(contact.name)
  const [phone, setPhone] = useState(contact.phone || '')
  const [isPhoneValid, setIsPhoneValid] = useState(true)
  const [email, setEmail] = useState(contact.email || '')
  const [alias, setAlias] = useState(contact.alias || '')
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [isPending, setIsPending] = useState(false)

  const isEmailEditable = contact.emailStatus === 'na' || contact.emailStatus === 'failure-editable-email'
  const isPhoneEditable = contact.smsStatus === 'na' || contact.smsStatus === 'failure-editable-phone'

  function validateForm(): boolean {
    if (!name.trim()) {
      return false
    }

    const hasPhone = phone.trim().length > 0
    const hasEmail = email.trim().length > 0

    if (!hasPhone && !hasEmail) {
      return false
    }

    if (hasPhone && !isPhoneValid) {
      return false
    }

    if (hasEmail && !isEmailValid(email)) {
      return false
    }

    return true
  }

  const isFormValid = validateForm()

  async function handleSubmit() {
    if (isFormValid) {
      setIsPending(true)
      await onSubmit({
        name,
        phone,
        email,
        alias,
      })
      setIsPending(false)
    }
  }

  return (
    <Section
      wrapperStyle='mt-8 mb-4'
      title='Update Contact'
      description='Add an additional contact method or fix an incorrect phone number or email address.'
    >
      <p className='mt-2 text-secondary-txt text-sm text-center italic'>
        Please note: You can only change an email or phone number if we were unable to send a message to it.
      </p>
      <div className='flex flex-col gap-4 sm:gap-5 md:gap-6 mt-8 mb-4'>
        {onDelete && contact?.id && (
          <Modal
            variant='danger'
            title='Delete Contact'
            message='Are you sure you want to delete this contact?'
            buttonLabel='Delete Contact'
            show={showDeleteModal}
            onConfirm={() => onDelete()}
            onClose={() => setShowDeleteModal(false)}
          />
        )}
        <div className='flex flex-col sm:grid lg:grid-cols-2 sm:items-end gap-4 sm:gap-5 md:gap-6'>
          <div className='w-full'>
            <Input
              id='name'
              label={t('content.components.input_contact.name_field_title')}
              labelStyle={labelStyle}
              type='text'
              placeholder='First Last'
              value={name}
              onInput={setName}
              inputStyle='focus:!ring-cta-active'
            />
          </div>
          <div className='w-full'>
            <Input
              id='alias'
              label={t('content.components.input_contact.nickname_field_title')}
              tooltip={<Tooltip content={t('content.components.input_contact.nickname_tooltip')} />}
              labelStyle={labelStyle}
              optional
              value={alias}
              placeholder={t('content.components.input_contact.nickname_field_placeholder')}
              onInput={setAlias}
              inputStyle='focus:!ring-cta-active'
            />
          </div>
          <div className='w-full'>
            <PhoneInput
              id='phone'
              disabled={!isPhoneEditable}
              label={t('content.components.input_contact.phone_field_title')}
              name='phone'
              value={phone}
              availableCountries={['US', 'CA', 'MX']}
              placeholder={t('content.components.input_contact.phone_field_placeholder')}
              labelStyle={labelStyle}
              borderStyle='!border-2 focus-within:!border-transparent focus-within:!ring-2 focus-within:!ring-cta-active !focus:outline'
              onChange={(value, meta: PhoneInputMeta) => {
                setPhone(value || '')
                setIsPhoneValid(meta.isValid)
              }}
            />
          </div>
          <div className='w-full'>
            <Input
              id='email'
              disabled={!isEmailEditable}
              label={t('content.components.input_contact.email_field_title')}
              labelStyle={labelStyle}
              optional
              value={email}
              placeholder={t('content.components.input_contact.email_field_placeholder')}
              onInput={setEmail}
              inputStyle='focus:!ring-cta-active'
            />
          </div>
        </div>

        <div className={`text-xs opacity-80   ${labelStyle}`}>
          *Text messages entered after 7pm PST will be sent the next day.
        </div>
        <div className='flex flex-row  justify-between pt-2 w-full mx-auto'>
          {onDelete && (
            <div className='w-1/6'>
              <Button label='Delete' onClick={() => setShowDeleteModal(true)} variant='danger' size='xl' />
            </div>
          )}
          <div className='w-4/6'>
            <Button
              label={isPending ? '' : 'Update Contact'}
              variant='cta'
              fullwidth
              size='xl'
              disabled={!isFormValid || isPending}
              icon={isPending ? <Spinner size='sm' /> : null}
              onClick={handleSubmit}
            />
          </div>
        </div>
      </div>
    </Section>
  )
}
